import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';

import { BootSplash } from './components/BootSplash';
import Sidebar from './components/Sidebar';
import Header from './components/Header';

// Lazy-loaded route views — reduces initial bundle by ~40%
const GalleryView = React.lazy(() => import('./components/GalleryView'));
const TasksView = React.lazy(() => import('./components/TasksView'));
const TeamView = React.lazy(() => import('./components/TeamView'));
const InsightsView = React.lazy(() => import('./components/InsightsView'));
const SettingsView = React.lazy(() => import('./components/SettingsView'));
import { Submission, Project, Participant, Task, AppData, TeamMemberRole, Invitation, ParticipantInvitation, ParticipantGroup, MediaType, TeamMember, Upload, Nudge } from './types';
import { VOICE_GUIDED_ENABLED } from './utils/voiceGuided';
import SubmissionModal from './components/SubmissionModal';
import ParticipantView from './components/ParticipantView';
import { deleteProject, deleteTask, deleteSubmission, deleteParticipant, createNudge, markNudgeAsRead, createSubmission, updateParticipantProfile, updateSubmission, batchUpdateSubmissions, updateTask, batchUpdateTasks, createOrUpdateProject, updateProject, createInvitation, deleteInvitation, createParticipantInvitation, batchCreateParticipantInvitations, deleteParticipantInvitation, deleteTeamMember, updateTeamMemberRole, createOrUpdateGroup, deleteGroup } from './services/firestoreService';
import { isPreviewSession } from './services/previewMode';
import { useAppData } from './contexts/AppDataContext';
import { requestNotificationPermission, onMessageListener } from './services/notificationService';
import LoginView from './components/LoginView';
import { useAuthContext } from './contexts/AuthContext';
import MagicLinkPendingView from './components/MagicLinkPendingView';
import AddProjectModal from './components/AddProjectModal';
import AddGroupModal from './components/AddGroupModal';
import ParticipantsView from './components/ParticipantsView';
import ShareSubmissionModal from './components/ShareSubmissionModal';
import ResearcherOnboarding from './components/ResearcherOnboarding';
import PermissionsPrompt from './components/PermissionsPrompt';
import ParticipantProfileModal from './components/ParticipantProfileModal';
import UploadManager from './components/UploadManager';
import UserInfoForm from './components/UserInfoForm';
const SuperAdminHub = React.lazy(() => import('./components/superadmin/SuperAdminHub'));
const SignalMapView = React.lazy(() => import('./components/SignalMapView'));
const DeliverablesView = React.lazy(() => import('./components/deliverables/DeliverablesView'));
const SharedReportView = React.lazy(() => import('./components/shared/SharedReportView'));
import { safeLocalStorage } from './utils/safeStorage';
import { SuperAdminGuard } from './guards/SuperAdminGuard';
import { CheckIcon, RefreshCwIcon, WifiOffIcon } from './components/icons';
import { sendBulkInvitationEmails } from './services/emailService';
import { updateUserProfile, signOut as authSignOut } from './services/authService';
import JoinProject, { JoinStatus } from './components/JoinProject';
import MissionControlView from './components/MissionControlView';
import DashboardView from './components/DashboardView';
import { useModalState } from './hooks/useModalState';
import { RouteErrorBoundary } from './components/common/RouteErrorBoundary';
import UnprovisionedView from './components/UnprovisionedView';
import { ParticipantNewPage, FeatureFlag } from './components/participant';
import { isFeatureEnabled } from './config/featureFlags';
import ProjectChooser from './components/ProjectChooser';
import { ResearcherFeed } from './components/researcher/feed';

export type View = 'dashboard' | 'tasks' | 'data' | 'feed' | 'participants' | 'team' | 'insights' | 'deliverables' | 'settings';
export type UserRole = 'researcher' | 'orgadmin' | 'participant';

export type PermissionQueryState = 'prompt' | 'granted' | 'denied' | 'checking';

// Map view names to URL paths and vice versa
const VIEW_TO_PATH: Record<View, string> = {
  dashboard: '/dashboard',
  tasks: '/tasks',
  data: '/data',
  feed: '/feed',
  participants: '/participants',
  team: '/team',
  insights: '/insights',
  deliverables: '/deliverables',
  settings: '/settings',
};


const App: React.FC = () => {
  const { user, userProfile, loading: authLoading, claimsRole, claimsOrgId, claimsKnown, refreshClaims, logout } = useAuthContext();
  const { appData, setAppData, isLoadingData, currentProjectId, setCurrentProjectId } = useAppData();
  const navigate = useNavigate();
  const location = useLocation();
  const [userRole, setUserRole] = useState<UserRole>('researcher');
  const [currentParticipant, setCurrentParticipant] = useState<Participant | null>(null);
  const [feedTheme, setFeedTheme] = useState<'dark' | 'light'>(() =>
    (safeLocalStorage.getItem('dekko-feed-theme') as 'dark' | 'light') || 'light'
  );

  // Modal state (extracted to reduce App.tsx line count)
  const {
    showUserInfoForm, setShowUserInfoForm,
    selectedSubmission, setSelectedSubmission,
    modalSeekTime, setModalSeekTime,
    modalHighlight, setModalHighlight,
    submissionToShare, setSubmissionToShare,
    isAddProjectModalOpen, setIsAddProjectModalOpen,
    showResearcherOnboarding, setShowResearcherOnboarding,
    viewingParticipant, setViewingParticipant,
    isGroupModalOpen, setIsGroupModalOpen,
    editingGroup, setEditingGroup,
  } = useModalState();

  // Join project state
  const [joinProjectId, setJoinProjectId] = useState<string | null>(null);
  const [isJoiningProject, setIsJoiningProject] = useState(false);
  const [joinStatus, setJoinStatus] = useState<JoinStatus>('joining');

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const handleShareSubmission = (submission: Submission) => {
    setSubmissionToShare(submission);
  };

  // Handler for switching from participant view back to researcher view
  const handleSwitchToResearcherView = () => {
    setUserRole('researcher');
    navigate('/feed');
  };

  const [permissionState, setPermissionState] = useState<PermissionQueryState>('checking');
  const [permissionError, setPermissionError] = useState<string | null>(null);

  const [activeUploads, setActiveUploads] = useState<Upload[]>([]);
  const [showGlobalSuccess, setShowGlobalSuccess] = useState<string | null>(null);
  const processedUploadIdsRef = useRef<Set<string>>(new Set());
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Pull to refresh state
  const [pullChange, setPullChange] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef(0);
  const REFRESH_THRESHOLD = 80;

  // Check for join URL on mount
  useEffect(() => {
    const path = window.location.pathname;
    const joinMatch = path.match(/^\/join\/(.+)$/);
    const joinAdminMatch = path.match(/^\/join-admin\/(.+)$/);

    if (joinMatch) {
      const projectId = joinMatch[1];
      // Store in localStorage so ParticipantNewPage picks it up
      safeLocalStorage.setItem('pendingProjectJoin', projectId);
      safeLocalStorage.setItem('pendingJoinRole', 'participant');
      // Sign out any existing session — join links are fresh entry points.
      // This ensures the login screen always shows for shared/WhatsApp links.
      // Direct visits to /p (bookmarks, PWA) keep their session.
      authSignOut().catch(() => {}).finally(() => {
        navigate('/p', { replace: true });
      });
      return;
    }

    // Admin join links (/join-admin/:id) still route through App's own
    // JoinProject flow below. Participant joins are owned by
    // ParticipantNewPage's handlePendingJoin effect — this component's own
    // post-auth arming effect (below) no longer touches the participant flow.
    if (joinAdminMatch) {
      const projectId = joinAdminMatch[1];
      setJoinProjectId(projectId);
      setIsJoiningProject(true);
      safeLocalStorage.setItem('pendingProjectJoin', projectId);
      safeLocalStorage.setItem('pendingJoinRole', 'admin');
    }
  }, []);

  // Post-auth: arm the ADMIN join flow only. Participant joins are owned by
  // ParticipantNewPage's handlePendingJoin — App's JoinProject UI (and its
  // onJoinComplete cleanup) is unreachable behind the `/p` short-circuit, so
  // arming participants here fired a duplicate joinProject Cloud Function
  // call and raced ParticipantNewPage for the pendingProjectJoin flag.
  useEffect(() => {
    if (!user || isJoiningProject) return;
    const pendingId = safeLocalStorage.getItem('pendingProjectJoin');
    const pendingRole = safeLocalStorage.getItem('pendingJoinRole');
    if (pendingId && pendingRole === 'admin') {
      setJoinProjectId(pendingId);
      setIsJoiningProject(true);
    }
  }, [user, isJoiningProject]);



  // Online/offline state for UI banners (upload queue handled by UploadManager)
  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Push Notifications Listener
  useEffect(() => {
    if (user) {
      onMessageListener().then((payload: any) => {
        console.log('Foreground message received:', payload);
        setShowGlobalSuccess(`Notification: ${payload.notification?.title || 'New update'}`);
        setTimeout(() => setShowGlobalSuccess(null), 5000);
      });
    }
  }, [user]);

  const toggleFeedTheme = useCallback(() => {
    setFeedTheme(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      safeLocalStorage.setItem('dekko-feed-theme', next);
      return next;
    });
  }, []);

  const handleEnableNotifications = async () => {
    if (user) {
      const granted = await requestNotificationPermission(user.uid);
      if (granted) {
        setShowGlobalSuccess('Notifications enabled successfully!');
      } else {
        setShowGlobalSuccess('Notification permission denied or failed.');
      }
      setTimeout(() => setShowGlobalSuccess(null), 4000);
    }
  };

  // Set user role from Firebase user profile and check if profile is complete
  useEffect(() => {
    if (claimsRole) {
      setUserRole(claimsRole);
      if (claimsRole === 'participant' && userProfile && !userProfile.profileComplete) {
        setShowUserInfoForm(true);
      } else {
        setShowUserInfoForm(false);
      }
    }
  }, [claimsRole, userProfile]);


  // Auto-associate participants when they login with invited email
  useEffect(() => {
    const handleInvitationAutoAssociation = async () => {
      if (!user || !userProfile || !appData) return;

      // Check if user email matches any pending invitations
      const matchingInvitations = appData.participantInvitations.filter(
        inv => inv.email.toLowerCase() === user.email?.toLowerCase() && inv.status === 'pending'
      );

      if (matchingInvitations.length > 0) {
        // Get the first invitation (in case there are multiple)
        const invitation = matchingInvitations[0];

        // Check if participant record already exists for this user+project combo
        const participantId = `${user.uid}-${invitation.projectId}`;
        const existingParticipant = appData.participants.find(p => p.id === participantId);

        if (!existingParticipant) {
          // Create participant record
          const newParticipant: Participant = {
            id: participantId, // Composite ID allows same user in multiple projects
            organizationId: invitation.organizationId || (claimsOrgId as string),
            name: userProfile.displayName || user.email?.split('@')[0] || 'User',
            avatarUrl: user.photoURL || '',
            status: 'active',
            projectId: invitation.projectId,
          };

          await updateParticipantProfile(participantId, newParticipant);
        }

        // Update user profile with participant info and project
        if (!userProfile.participantId || !userProfile.projectId) {
          // First time joining - trigger profile setup only if not already complete
          await updateUserProfile(user.uid, {
            role: 'participant',
            participantId: user.uid,
            projectId: invitation.projectId,
            profileComplete: userProfile.profileComplete === true ? true : false,
          });
        } else {
          // Already a participant - just update projectId to latest (or keep existing)
          // Don't change profileComplete
          await updateUserProfile(user.uid, {
            projectId: invitation.projectId, // Update to latest project joined
          });
        }

        // Remove the processed invitation
        await deleteParticipantInvitation(invitation.id);
      }
    };

    handleInvitationAutoAssociation();
  }, [user, userProfile, appData]);

  // Auto-join project when user visits /join/:projectId URL
  useEffect(() => {
    const handleProjectJoin = async () => {
      if (!user) return;

      try {
        // Check for pending project join (from URL or localStorage)
        const pendingProjectId = joinProjectId || safeLocalStorage.getItem('pendingProjectJoin');
        if (!pendingProjectId) return;

        setJoinStatus('joining');

        const pendingRole = safeLocalStorage.getItem('pendingJoinRole') || 'participant';

        // Check if already joined based on role
        if (pendingRole === 'participant' && userProfile?.projectId === pendingProjectId && claimsRole === 'participant') {
          setJoinStatus('already-joined');
          return;
        }

        const teamMemberId = `${user.uid}-${pendingProjectId}`;
        if (pendingRole === 'admin' && appData.teamMembers?.some(m => m.id === teamMemberId && (m.role as string) === 'admin')) {
          setJoinStatus('already-joined');
          return;
        }

        try {
          // Call the Cloud Function with role
          const functions = getFunctions();
          const joinProjectFn = httpsCallable(functions, 'joinProject');

          await joinProjectFn({ projectId: pendingProjectId, role: pendingRole });

          // Update user profile based on join role
          if (pendingRole === 'admin') {
            await updateUserProfile(user.uid, {
              role: 'researcher',
              projectId: pendingProjectId,
            });
          } else {
            await updateUserProfile(user.uid, {
              role: 'participant',
              participantId: user.uid,
              projectId: pendingProjectId,
            });
          }

          // Force client to fetch the new JWT Claims immediately
          await refreshClaims();

          setJoinStatus('success');

        } catch (error) {
          console.error('Error from Cloud Function joinProject:', error);
          setJoinStatus('error');
        }
      } catch (error) {
        console.error('CRITICAL: Unhandled error in handleProjectJoin:', error);
        setJoinStatus('error');
      }
    };

    if (isJoiningProject) {
      handleProjectJoin();
    }
  }, [user, userProfile, joinProjectId, isJoiningProject]);



  // Initialize currentParticipant when switching to participant role
  useEffect(() => {
    if (userRole === 'participant' && user && appData) {
      // For actual participants, use their assigned projectId from profile
      // For researchers previewing, use the currently selected project
      // Fallback: old-flow participants may lack userProfile.projectId but have a participant record
      let projectId = userProfile?.projectId || currentProjectId;
      if (!projectId) {
        const prefix = `${user.uid}-`;
        const record = appData.participants.find(p => p.id.startsWith(prefix) && p.projectId);
        if (record) projectId = record.projectId;
      }

      if (!projectId) {
        // No project available, can't create participant
        setCurrentParticipant(null);
        return;
      }

      // Find participant record for this user+project combo
      const participantId = `${user.uid}-${projectId}`;
      const existingParticipant = appData.participants.find(
        p => p.id === participantId
      );

      if (existingParticipant) {
        setCurrentParticipant(existingParticipant);
      } else {
        const projectOrgId = appData.projects.find(p => p.id === projectId)?.organizationId;
        // Create a new participant record from user info
        const newParticipant: Participant = {
          id: participantId,
          organizationId: projectOrgId || (claimsOrgId as string),
          name: userProfile?.displayName || user.displayName || user.email?.split('@')[0] || 'User',
          avatarUrl: user.photoURL || '',
          status: 'active',
          projectId: projectId,
          location: '',
          occupation: '',
        };
        setCurrentParticipant(newParticipant);
        // Hold it in state only when an admin is previewing — writing here
        // created a real participant record for a researcher who never joined
        // the study, which then appeared in participant lists and counts.
        if (!isPreviewSession()) {
          updateParticipantProfile(participantId, newParticipant);
        }
      }
    } else {
      setCurrentParticipant(null);
    }
  }, [userRole, user, userProfile, appData, currentProjectId]);

  // Self-healing: Merge duplicate participant records for same user
  // This fixes existing participants whose data was saved to orphaned records
  useEffect(() => {
    if (!user || userRole !== 'participant' || !appData) return;

    const fixParticipantData = async () => {
      const myRecords = appData.participants.filter(p => p.id.startsWith(user.uid));

      if (myRecords.length > 1) {
        // Find the record that has profile info (age, occupation, etc.)
        const profileRecord = myRecords.find(p => p.age !== undefined || p.occupation !== undefined);
        // Find the record that is linked to a project (has projectId)
        const projectRecord = myRecords.find(p => p.projectId && p.projectId !== '');

        // If we have both, and they are different records
        if (profileRecord && projectRecord && profileRecord.id !== projectRecord.id) {

          // Update the project record with the profile data
          const updatedParticipant = {
            ...projectRecord,
            age: profileRecord.age,
            gender: profileRecord.gender,
            location: profileRecord.location,
            occupation: profileRecord.occupation,
            // Use profile name/avatar if available as they are likely more recent
            name: profileRecord.name || projectRecord.name,
            avatarUrl: profileRecord.avatarUrl || projectRecord.avatarUrl,
          };

          // Update the merged participant and delete the orphaned record
          await updateParticipantProfile(projectRecord.id, updatedParticipant);
          await deleteParticipant(profileRecord.id);

          // Update current participant if needed
          if (currentParticipant?.id === projectRecord.id) {
            setCurrentParticipant(updatedParticipant);
          }
        }
      }
    };

    // Run this check once when appData loads
    fixParticipantData();
  }, [user, userRole, appData?.participants.length]); // Only run when participants change

  useEffect(() => {
    const checkPermissions = async () => {
      // On insecure contexts (HTTP over LAN), navigator.mediaDevices is undefined.
      // Skip permission checks entirely — the browser will prompt when media is actually needed,
      // or the app will show an appropriate error at capture time.
      if (!navigator.mediaDevices) {
        console.warn('mediaDevices unavailable (insecure context). Skipping permission check.');
        setPermissionState('granted');
        return;
      }
      try {
        const cameraStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });
        const micStatus = await navigator.permissions.query({ name: 'microphone' as PermissionName });
        if (cameraStatus.state === 'granted' && micStatus.state === 'granted') {
          setPermissionState('granted');
        } else if (cameraStatus.state === 'prompt' || micStatus.state === 'prompt') {
          setPermissionState('prompt');
        } else {
          setPermissionState('denied');
        }
      } catch (error) {
        console.error("Permission query failed:", error);
        // If permissions API isn't available, assume granted and let capture-time errors handle it
        setPermissionState('granted');
      }
    };
    if (userRole === 'participant' && user) {
      checkPermissions();
    }
  }, [userRole, user]);

  const createFinalSubmission = useCallback(async (payload: Omit<Submission, 'id'>) => {
    if (!appData) return;

    // Check if a submission with the same taskId and participantId already exists (prevent duplicates)
    if (payload.taskId) {
      const existingSubmission = appData.submissions.find(
        s => s.taskId === payload.taskId && s.participantId === payload.participantId
      );
      if (existingSubmission) {
        // Update existing submission instead of creating duplicate.
        // Offline, Firestore's persistent cache queues the write and replays it
        // on reconnect — but the promise doesn't settle until then, so fire it
        // without awaiting rather than hanging the handler. (This branch used to
        // return early having written nothing, while claiming it would sync.)
        if (!navigator.onLine) {
          updateSubmission(existingSubmission.id, { ...payload, timestamp: new Date().toISOString() })
            .catch(err => console.error('Queued resubmit failed:', err));
          setShowGlobalSuccess('Saved offline — will sync when you reconnect.');
          setTimeout(() => setShowGlobalSuccess(null), 4000);
          return;
        }

        await updateSubmission(existingSubmission.id, { ...payload, timestamp: new Date().toISOString() });
        setShowGlobalSuccess('Task resubmitted successfully!');
        setTimeout(() => setShowGlobalSuccess(null), 4000);
        return;
      }
    }

    try {
      if (!navigator.onLine) {
        // Durably queued by Firestore's persistent cache, so "will sync" is now
        // true — it wasn't when this wrote to the dead submissions_queue. Not
        // awaited: the promise only settles once the server acks.
        createSubmission(payload).catch(err => console.error('Queued submission failed:', err));
        setShowGlobalSuccess('Saved offline — will sync when you reconnect.');
      } else {
        const submissionId = await createSubmission(payload);
        // Optimistic update for the UI
        const newSubmission: Submission = {
          ...payload,
          id: submissionId,
        };
        setAppData(prev => prev ? { ...prev, submissions: [newSubmission, ...prev.submissions] } : null);
        setShowGlobalSuccess('Task submitted successfully!');
      }
    } catch (error) {
      console.error('Error in createFinalSubmission:', error);
      setShowGlobalSuccess('Failed to submit task. Please try again.');
    }

    setTimeout(() => setShowGlobalSuccess(null), 4000);
  }, [appData]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveUploads(prevUploads => {
        const uploadToProcess = prevUploads.find(u => u.status === 'uploading' && !processedUploadIdsRef.current.has(u.id));

        if (!uploadToProcess) {
          return prevUploads;
        }

        const newProgress = Math.min(uploadToProcess.progress + 20, 100);

        if (newProgress === 100) {
          // Mark as processed immediately to prevent duplicate submissions
          processedUploadIdsRef.current.add(uploadToProcess.id);

          // Mark as complete in state
          const updatedUploads = prevUploads.map(u =>
            u.id === uploadToProcess.id ? { ...u, progress: 100, status: 'complete' } : u
          );

          // Create submission asynchronously
          createFinalSubmission(uploadToProcess.payload).then(() => {
            // Remove from processed set after a delay to allow cleanup
            setTimeout(() => {
              processedUploadIdsRef.current.delete(uploadToProcess.id);
            }, 10000);
          });

          const finishedUploadId = uploadToProcess.id;
          setTimeout(() => {
            setActiveUploads(current => current.filter(u => u.id !== finishedUploadId));
          }, 5000);

          return updatedUploads;
        } else {
          return prevUploads.map(u => u.id === uploadToProcess.id ? { ...u, progress: newProgress } : u);
        }
      });
    }, 500);

    return () => clearInterval(interval);
  }, [createFinalSubmission]);


  const requestPermissions = async () => {
    // Guard against insecure contexts where mediaDevices is unavailable
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.warn('mediaDevices unavailable (insecure context). Granting permissions by default.');
      setPermissionState('granted');
      setPermissionError(null);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      stream.getTracks().forEach(track => track.stop());
      setPermissionState('granted');
      setPermissionError(null);
    } catch (err) {
      console.error("Error requesting permissions:", err);
      if (err instanceof Error) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setPermissionState('denied');
          setPermissionError("You denied permission. Please enable it in your browser settings.");
        } else {
          setPermissionError(`An unexpected error occurred: ${err.message}. Please try again.`);
        }
      }
    }
  };

  // Check for researcher onboarding
  useEffect(() => {
    if (user && userRole === 'researcher' && appData) {
      const hasOnboarded = safeLocalStorage.getItem('hasCompletedResearcherOnboarding');
      if (!hasOnboarded) {
        // setShowResearcherOnboarding(true);
      }
    }
  }, [user, userRole, appData]);

  const handleUpdateParticipantProfile = async (updatedProfile: Participant) => {
    if (!appData) return;
    await updateParticipantProfile(updatedProfile.id, updatedProfile);
    setCurrentParticipant(updatedProfile);
  };

  const handleUserInfoComplete = async (participantData: Omit<Participant, 'id'>) => {
    if (!user || !userProfile) return;

    try {
      const projectId = joinProjectId || safeLocalStorage.getItem('pendingProjectJoin') || userProfile.projectId;
      if (!projectId) {
        console.error('No project ID found to complete profile');
        return;
      }

      const participantId = `${user.uid}-${projectId}`;

      // 1. Update the participant document directly in Firestore
      await updateParticipantProfile(participantId, {
        name: participantData.name,
        avatarUrl: participantData.avatarUrl,
        age: participantData.age,
        gender: participantData.gender,
        location: participantData.location,
        occupation: participantData.occupation,
      });
      // 2. Mark the user profile as complete (stripping role, participantId, projectId)
      await updateUserProfile(user.uid, {
        profileComplete: true,
      });
      // 3. Mark the join flow as totally complete
      setJoinStatus('success');
      setShowUserInfoForm(false);

      // 4. Clean up join flow state
      safeLocalStorage.removeItem('pendingProjectJoin');
      safeLocalStorage.removeItem('pendingJoinRole');
      setIsJoiningProject(false);

    } catch (error) {
      console.error('Error saving user info:', error);
      setJoinStatus('error');
    }
  };

  const handleCreateSubmission = (
    submissionData: { notes: string; mediaUrls: string[]; mediaType: MediaType },
    taskPrompt: string,
    taskId?: string
  ) => {
    if (!currentParticipant) return;

    const payload: Omit<Submission, 'id'> = {
      projectId: currentProjectId,
      participantId: currentParticipant.id,
      organizationId: currentParticipant.organizationId || (claimsOrgId as string),
      mediaUrls: submissionData.mediaUrls,
      mediaType: submissionData.mediaType,
      timestamp: new Date().toISOString(),
      notes: submissionData.notes,
      tags: [],
      location: currentParticipant.location || "Unknown Location",
      taskId: taskId,
    };

    const newUpload: Upload = {
      id: `upload-${Date.now()}`,
      taskPrompt,
      progress: 0,
      status: 'uploading',
      payload,
    };
    setActiveUploads(prev => [...prev, newUpload]);
  };

  const currentProject = useMemo(() => {
    if (!appData) return null;
    // Match by saved ID
    const matched = appData.projects.find(p => p.id === currentProjectId);
    if (matched) return matched;
    // Only auto-fallback if there's a stale saved ID (project was deleted) — not if empty
    if (currentProjectId && appData.projects.length > 0) return appData.projects[0];
    return null;
  }, [appData, currentProjectId]);
  const currentResearcher = useMemo(() => {
    if (!appData) return null;

    // First try to find the actual logged-in user in team members
    if (user) {
      const match = appData.teamMembers.find(m => m.id === user.uid);
      if (match) return match;

      // If user is logged in as a non-participant but has no DB record yet, construct one from auth
      if (userRole !== 'participant') {
        return {
          id: user.uid,
          name: user.displayName || user.email?.split('@')[0] || 'Researcher',
          email: user.email || '',
          avatarUrl: user.photoURL || null,
          role: TeamMemberRole.Admin
        } as TeamMember;
      }
    }

    // Fallback for development/testing if no real user is present to avoid confusion
    // Using 'user-1' only if no real user is present to avoid confusion
    return !user ? appData.teamMembers.find(m => m.id === 'user-1') : null;
  }, [appData, user, userRole]);


  const { projectSubmissions, projectTasks, projectParticipants, projectParticipantsMap } = useMemo(() => {
    if (!appData) return { projectSubmissions: [], projectTasks: [], projectParticipants: [], projectParticipantsMap: new Map() };

    // For participants: show tasks from ALL projects they're in
    if (userRole === 'participant' && user) {
      const participantProjectIds = appData.participants
        .filter(p => p.id.startsWith(`${user.uid}-`))
        .map(p => p.projectId);

      const participantTasks = appData.tasks.filter(t => participantProjectIds.includes(t.projectId));
      const participantSubmissions = appData.submissions.filter(s => participantProjectIds.includes(s.projectId));
      const participantParticipants = appData.participants.filter(p => participantProjectIds.includes(p.projectId));
      const participantParticipantsMap = new Map(participantParticipants.map(p => [p.id, p]));

      return {
        projectSubmissions: participantSubmissions,
        projectTasks: participantTasks,
        projectParticipants: participantParticipants,
        projectParticipantsMap: participantParticipantsMap
      };
    }

    // For researchers: show data from current project only
    const projectSubmissions = appData.submissions.filter(s => s.projectId === currentProjectId);
    const projectTasks = appData.tasks.filter(t => t.projectId === currentProjectId);
    // Filter participants to only show those belonging to current project
    const projectParticipants = appData.participants.filter(p => p.projectId === currentProjectId);
    const projectParticipantsMap = new Map(projectParticipants.map(p => [p.id, p]));
    return { projectSubmissions, projectTasks, projectParticipants, projectParticipantsMap };
  }, [appData, currentProjectId, userRole, user]);

  const hasSignificationTasks = useMemo(() =>
    projectTasks.some(t => t.mediaTypes.includes(MediaType.Signification)),
    [projectTasks]
  );

  const displayedTeamMembers = useMemo(() => {
    if (!appData) return [];

    // Start with existing team members
    const members = [...appData.teamMembers];
    const memberIds = new Set(members.map(m => m.id));

    // Add researchers/admins from the users collection if they're not already in team members
    if (appData.users) {
      appData.users.forEach(u => {
        if (!memberIds.has(u.uid)) {
          // Map UserRole to TeamMemberRole
          let teamRole = TeamMemberRole.Viewer;
          if (u.role === 'superadmin') teamRole = TeamMemberRole.Admin;
          if (u.role === 'researcher') teamRole = TeamMemberRole.Researcher;

          if (teamRole !== TeamMemberRole.Viewer) {
            members.push({
              id: u.uid,
              name: u.displayName || u.email.split('@')[0],
              email: u.email,
              avatarUrl: '', // UserProfile doesn't have avatarUrl yet, use empty string or placeholder
              role: teamRole
            });
            memberIds.add(u.uid);
          }
        }
      });
    }

    // Ensure current user is in the list as Admin if they are not already there
    // This is a safety fallback for the currently logged in user
    if (currentResearcher && !memberIds.has(currentResearcher.id)) {
      members.push(currentResearcher);
    }

    return members;
  }, [appData, currentResearcher]);

  const handleUpdateTags = async (submissionId: string, newTags: string[]) => {
    if (!appData) return;
    const currentSubmission = appData.submissions.find(s => s.id === submissionId);
    if (!currentSubmission) return;
    const mergedTags = [...new Set([...currentSubmission.tags, ...newTags])];
    await updateSubmission(submissionId, { tags: mergedTags });
  };

  const handleUpdateNotes = async (submissionId: string, notes: string) => {
    if (!appData) return;
    await updateSubmission(submissionId, { notes });
  };

  const handleUpdateTranscript = async (submissionId: string, transcript: string) => {
    if (!appData) return;
    await updateSubmission(submissionId, { transcript });
  };

  const handleCitationClick = (participantName: string, timeStr: string, quote: string) => {
    if (!appData) return;
    const participant = appData.participants.find(p => p.name.toLowerCase() === participantName.toLowerCase());
    if (participant) {
      // Find most relevant submission (audio/video or just the first one)
      const submission = appData.submissions.find(s => s.participantId === participant.id && (s.mediaType === 'video' || s.mediaType === 'audio'))
        || appData.submissions.find(s => s.participantId === participant.id);
      if (submission) {
        setModalSeekTime(timeStr);
        setModalHighlight(quote);
        setSelectedSubmission(submission);
      } else {
        console.warn(`No submission found for ${participantName}`);
      }
    } else {
      console.warn(`Participant ${participantName} not found.`);
    }
  };

  const handleDeleteSubmission = async (submissionId: string) => {
    if (!appData) return;
    try {
      await deleteSubmission(submissionId);
      const newSubmissions = appData.submissions.filter(s => s.id !== submissionId);
      const updatedAppData = { ...appData, submissions: newSubmissions };
      setAppData(updatedAppData); // Optimistic update
    } catch (error) {
      console.error('Error deleting submission:', error);
      alert('Failed to delete submission');
    }
  };

  const handleSaveTask = async (taskToSave: Task) => {
    if (!appData) return;

    /* Nobody assigned → hold it back as a draft. Written on every save, not
       just when empty, so assigning someone later clears the flag and
       publishes the task. */
    const finalTaskToSave: Task = {
      ...taskToSave,
      organizationId: taskToSave.organizationId || (claimsOrgId as string),
      isDraft: (taskToSave.assignedParticipantIds?.length ?? 0) === 0,
    };

    /* Who is newly on this task. Computed BEFORE the write: the tasks snapshot
       is debounced ~500ms and would otherwise already carry the new assignment
       list as the "previous" one. Diffing rather than notifying every assignee
       means editing a task's wording doesn't re-ping people who already have it. */
    const previouslyAssigned = new Set(
      appData.tasks.find(t => t.id === finalTaskToSave.id)?.assignedParticipantIds || []
    );
    const newlyAssigned = finalTaskToSave.status === 'active'
      ? (finalTaskToSave.assignedParticipantIds || []).filter(id => !previouslyAssigned.has(id))
      : [];

    await updateTask(finalTaskToSave.id, finalTaskToSave);

    /* Tell them. `nudges` already backs ParticipantInbox, and a nudge carrying
       a taskId renders a task chip with a "Go to task" CTA — no new UI or
       collection needed. A failure here must not read as a failed save: the
       task is already written and visible. */
    if (newlyAssigned.length > 0 && user) {
      try {
        await Promise.all(newlyAssigned.map(participantId => createNudge({
          organizationId: finalTaskToSave.organizationId || '',
          projectId: finalTaskToSave.projectId,
          participantId,
          message: `New task for you: ${finalTaskToSave.title || finalTaskToSave.prompt}`,
          sentBy: user.uid,
          sentAt: new Date().toISOString(),
          isRead: false,
          taskId: finalTaskToSave.id,
        })));
      } catch (error) {
        console.error('[App] Task saved but inbox message failed:', error);
      }
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!appData) return;
    try {
      await deleteTask(taskId);
      const newTasks = appData.tasks.filter(t => t.id !== taskId);
      const updatedAppData = { ...appData, tasks: newTasks };
      setAppData(updatedAppData); // Optimistic update
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task');
    }
  };

  const handleCreateProject = async (projectData: Omit<Project, 'id'>) => {
    if (!appData) return;
    const projectId = `proj-${Date.now()}`;
    const newProject = {
      ...projectData,
      organizationId: claimsOrgId as string
    };
    await createOrUpdateProject(projectId, newProject);
    setCurrentProjectId(projectId);
    setIsAddProjectModalOpen(false);
  };

  const handleSaveProject = async (projectToSave: Project) => {
    if (!appData) return;
    await updateProject(projectToSave.id, projectToSave);
  };

  const handleDeleteProject = async (projectId: string) => {
    if (!appData) return;

    try {
      // Delete from Firebase
      await deleteProject(projectId);

      // Update local state by removing the project
      const newProjects = appData.projects.filter(p => p.id !== projectId);

      // If there are other projects, switch to the first one
      if (newProjects.length > 0) {
        setCurrentProjectId(newProjects[0].id);
      } else {
        // No projects left, clear current project
        setCurrentProjectId('');
      }

      // Navigate to dashboard
      navigate('/feed');
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Failed to delete project. Please try again.');
    }
  };

  const handleInviteTeamMember = async (email: string, role: TeamMemberRole) => {
    if (!appData) return;
    const invitationId = `invite-${Date.now()}`;
    const newInvitation = {
      email,
      role,
      status: 'pending' as const,
      organizationId: claimsOrgId as string
    };
    await createInvitation(invitationId, newInvitation);
  };

  const handleRemoveTeamMember = async (memberId: string) => {
    if (!appData) return;
    await deleteTeamMember(memberId);
  };

  const handleChangeTeamMemberRole = async (memberId: string, newRole: TeamMemberRole) => {
    if (!appData) return;
    await updateTeamMemberRole(memberId, newRole);
  };

  const handleRevokeTeamInvite = async (inviteId: string) => {
    if (!appData) return;
    await deleteInvitation(inviteId);
  };

  const handleInviteParticipants = async (emails: string[]) => {
    if (!appData || !currentProject || !userProfile) return;

    // Create invitation records
    const newInvites = emails.map(email => ({
      id: `part-invite-${Date.now()}-${Math.random()}`,
      data: {
        email,
        projectId: currentProject.id,
        status: 'pending' as const,
        invitedAt: new Date().toISOString(),
        organizationId: claimsOrgId as string
      }
    }));

    // Save invitations to database
    await batchCreateParticipantInvitations(newInvites);

    // Send invitation emails
    try {
      const inviteLink = `${window.location.origin}/join/${currentProject.id}`;

      const result = await sendBulkInvitationEmails(
        emails,
        currentProject.name,
        currentProject.id,
        userProfile.displayName || userProfile.email,
        inviteLink
      );

      console.log(`Invitation emails: ${result.sent} sent, ${result.failed} failed`);

      if (result.failed > 0) {
        console.error('Some emails failed to send:', result.errors);
      }
    } catch (error) {
      console.error('Error sending invitation emails:', error);
      // Don't fail the whole operation if email sending fails
      // The invitations are still created in the database
    }
  };

  const handleRemoveParticipant = async (participantId: string) => {
    if (!appData) return;

    try {
      await deleteParticipant(participantId);

      const newParticipants = appData.participants.filter(p => p.id !== participantId);
      const newSubmissions = appData.submissions.filter(s => s.participantId !== participantId);

      const updatedAppData = { ...appData, participants: newParticipants, submissions: newSubmissions };
      setAppData(updatedAppData);
    } catch (error) {
      console.error('Error deleting participant:', error);
      alert('Failed to delete participant');
    }
  };

  const handleRevokeParticipantInvite = async (inviteId: string) => {
    if (!appData) return;
    await deleteParticipantInvitation(inviteId);
  };

  const handleSaveGroup = async (groupToSave: ParticipantGroup) => {
    if (!appData) return;
    await createOrUpdateGroup(groupToSave.id, groupToSave);
  };

  const handleOpenGroupModal = (group?: ParticipantGroup) => {
    setEditingGroup(group || null);
    setIsGroupModalOpen(true);
  };

  const handleSaveGroupWithClose = async (groupData: Omit<ParticipantGroup, 'id'> & { id?: string }) => {
    const groupToSave = {
      ...groupData,
      id: groupData.id || `group-${Date.now()}`,
      organizationId: groupData.organizationId || (claimsOrgId as string)
    };
    try {
      await createOrUpdateGroup(groupToSave.id, groupToSave);
      setIsGroupModalOpen(false);
    } catch (err) {
      console.error('Failed to save group:', err);
      alert('Failed to save group. Please try again.');
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    if (!appData) return;
    await deleteGroup(groupId);
  };

  const handleStartTour = () => {
    setShowResearcherOnboarding(true);
    navigate('/feed');
  };

  const handleOnboardingComplete = () => {
    setShowResearcherOnboarding(false);
    safeLocalStorage.setItem('hasCompletedResearcherOnboarding', 'true');
  };

  const refreshData = async () => {
    setIsRefreshing(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsRefreshing(false);
    setPullChange(0);
  };

  const handleUpdateSubmission = async (submissionId: string, updates: Partial<Submission>) => {
    if (!appData) return;
    setAppData(prev => prev ? {
      ...prev,
      submissions: prev.submissions.map(s => s.id === submissionId ? { ...s, ...updates } : s),
    } : prev);
    if (selectedSubmission?.id === submissionId) {
      setSelectedSubmission({ ...selectedSubmission, ...updates });
    }
    await updateSubmission(submissionId, updates);
  };

  const handleBatchUpdate = async (ids: string[], updates: Partial<Submission>) => {
    if (!appData) return;
    await batchUpdateSubmissions(ids.map(id => ({ id, data: updates })));
  };

  const handleBatchUpdateTasks = async (ids: string[], updates: Partial<Task>) => {
    if (!appData) return;
    await batchUpdateTasks(ids.map(id => ({ id, data: updates })));
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (scrollContainerRef.current && scrollContainerRef.current.scrollTop === 0) {
      touchStartY.current = e.targetTouches[0].clientY;
    } else {
      touchStartY.current = 0;
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartY.current === 0) return;
    if (scrollContainerRef.current && scrollContainerRef.current.scrollTop > 0) return;

    const currentY = e.targetTouches[0].clientY;
    const diff = currentY - touchStartY.current;

    if (diff > 0) {
      const resistance = 0.4;
      setPullChange(Math.min(diff * resistance, 150));
    }
  };

  const handleTouchEnd = () => {
    if (pullChange > REFRESH_THRESHOLD) {
      setPullChange(REFRESH_THRESHOLD);
      refreshData();
    } else {
      setPullChange(0);
    }
    touchStartY.current = 0;
  };

  const renderCurrentView = () => {
    if (!appData) return null;

    // SuperAdmin Hub relies purely on Claims, not on a project context
    if (location.pathname.startsWith('/superadmin')) {
      return (
        <RouteErrorBoundary key={location.pathname}>
          <React.Suspense fallback={<div className="flex h-full items-center justify-center"><div className="w-8 h-8 border-3 border-t-action border-action/20 rounded-full animate-spin"></div></div>}>
          <Routes>
            <Route path="/superadmin/*" element={<SuperAdminGuard><SuperAdminHub /></SuperAdminGuard>} />
          </Routes>
          </React.Suspense>
        </RouteErrorBoundary>
      );
    }

    // If no project is selected yet for admin users, still render routes
    // (dashboard handles empty state; prevents blank page on login)

    if (userRole === 'participant') {
      // Feature flag: use new participant UI when enabled
      if (isFeatureEnabled('useNewParticipantUI')) {
        return <ParticipantNewPage />;
      }

      if (!currentParticipant) {
        // Still initializing participant, show loading
        return (
          <div className="flex h-full items-center justify-center">
            <div className="w-12 h-12 border-4 border-t-action border-action/20 rounded-full animate-spin"></div>
            <p className="ml-4 text-text-muted font-bold">Loading...</p>
          </div>
        );
      }
      if (permissionState !== 'granted') {
        return <PermissionsPrompt onAllow={requestPermissions} error={permissionError} permissionState={permissionState} />;
      }
      // Filter submissions for current participant
      const participantSubmissions = projectSubmissions.filter(s => s.participantId === currentParticipant.id);
      const participantNudges = appData?.nudges.filter(n => n.participantId === currentParticipant.id) || [];

      return <ParticipantView
        currentProject={currentProject}
        tasks={projectTasks}
        onCreateSubmission={handleCreateSubmission}
        currentUser={currentParticipant}
        onUpdateProfile={handleUpdateParticipantProfile}
        submissions={participantSubmissions}
        onSelectSubmission={setSelectedSubmission}
        onDeleteSubmission={handleDeleteSubmission}
        nudges={participantNudges}
        onMarkNudgeAsRead={async (nudgeId) => await markNudgeAsRead(nudgeId)}
      />;
    }

    const isInsights = location.pathname === '/insights';

    return (
      <>
      {/* InsightsView stays mounted permanently so Analysis Grid state survives navigation */}
      {currentProjectId && (
        <div style={{ display: isInsights ? 'flex' : 'none', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {/* InsightsView is lazy — without a boundary here a slow chunk
              suspends with no fallback, which is a React error that lands in
              GlobalErrorBoundary (manual reload only). */}
          <React.Suspense fallback={<div className="flex h-full items-center justify-center"><div className="w-8 h-8 border-3 border-t-action border-action/20 rounded-full animate-spin"></div></div>}>
            <InsightsView
              submissions={projectSubmissions}
              participants={projectParticipants}
              groups={appData.participantGroups}
              tasks={projectTasks}
              projectId={currentProjectId}
            />
          </React.Suspense>
        </div>
      )}
      {!isInsights && (
      <RouteErrorBoundary key={location.pathname}>
        <React.Suspense fallback={<div className="flex h-full items-center justify-center"><div className="w-8 h-8 border-3 border-t-action border-action/20 rounded-full animate-spin"></div></div>}>
        <Routes>
          <Route path="/dashboard" element={
            <DashboardView
              submissions={projectSubmissions}
              tasks={projectTasks}
              participants={projectParticipants}
              participantsMap={projectParticipantsMap}
              participantGroups={appData.participantGroups}
              setSelectedSubmission={setSelectedSubmission}
              totalParticipantCount={projectParticipants.length}
              currentProject={currentProject}
            />
          } />
          <Route path="/data" element={
            <MissionControlView
              projectId={currentProjectId!}
              setSelectedSubmission={setSelectedSubmission}
              tasks={projectTasks}
              onUpdateSubmission={handleUpdateSubmission}
              onBatchUpdate={handleBatchUpdate}
              participantGroups={appData.participantGroups}
              users={appData.users}
              onShowSuccess={(msg) => {
                setShowGlobalSuccess(msg);
                setTimeout(() => setShowGlobalSuccess(null), 4000);
              }}
            />
          } />
          <Route path="/feed" element={
            <ResearcherFeed
              submissions={projectSubmissions}
              tasks={projectTasks}
              participants={projectParticipants}
              participantsMap={projectParticipantsMap}
              participantGroups={appData.participantGroups}
              threadContext={{
                currentUserUid: user!.uid,
                currentUserName: userProfile?.displayName || user!.displayName || 'Researcher',
                organizationId: claimsOrgId as string,
              }}
              onUpdateSubmission={handleUpdateSubmission}
              theme={feedTheme}
              onToggleTheme={toggleFeedTheme}
            />
          } />
          <Route path="/tasks" element={
            <TasksView
              tasks={projectTasks}
              participants={projectParticipants}
              participantGroups={appData.participantGroups}
              onSaveTask={handleSaveTask}
              onBatchUpdateTasks={handleBatchUpdateTasks}
              onDeleteTask={handleDeleteTask}
              currentProjectId={currentProjectId}
              totalParticipantCount={projectParticipants.length}
              defaultMediaTypes={currentProject?.defaultMediaTypes}
              defaultCollectionMode={currentProject?.defaultCollectionMode}
              participantUiMode={VOICE_GUIDED_ENABLED ? currentProject?.participantUiMode : undefined}
              onShowSuccess={(msg) => {
                setShowGlobalSuccess(msg);
                setTimeout(() => setShowGlobalSuccess(null), 4000);
              }}
            />
          } />
          <Route path="/participants" element={
            <ParticipantsView
              participants={projectParticipants}
              invitations={appData.participantInvitations}
              groups={appData.participantGroups}
              onInvite={handleInviteParticipants}
              onRemove={handleRemoveParticipant}
              onRevoke={handleRevokeParticipantInvite}
              onSaveGroup={handleSaveGroup}
              onDeleteGroup={handleDeleteGroup}
              currentProject={currentProject}
              onViewProfile={setViewingParticipant}
              onOpenGroupModal={handleOpenGroupModal}
            />
          } />
          <Route path="/team" element={
            <TeamView
              teamMembers={displayedTeamMembers}
              invitations={appData.invitations}
              onInvite={handleInviteTeamMember}
              onRemove={handleRemoveTeamMember}
              onChangeRole={handleChangeTeamMemberRole}
              onRevoke={handleRevokeTeamInvite}
              currentUserId={user?.uid}
              currentProjectId={currentProjectId}
            />
          } />
          {/* InsightsView rendered permanently below — route placeholder only */}
          <Route path="/insights" element={null} />
          <Route path="/deliverables" element={
            <DeliverablesView projectId={currentProjectId || ''} />
          } />
          <Route path="/settings" element={
            <SettingsView currentProject={currentProject} onSaveProject={handleSaveProject} onDeleteProject={handleDeleteProject} />
          } />
          <Route path="/signal-map" element={
            <SignalMapView
              projectId={currentProjectId!}
              projectName={currentProject?.name || ''}
              tasks={projectTasks}
              submissions={projectSubmissions}
              participants={projectParticipants}
              participantGroups={appData.participantGroups}
            />
          } />
          <Route path="*" element={<Navigate to="/feed" replace />} />
        </Routes>
        </React.Suspense>
      </RouteErrorBoundary>
      )}
      </>
    );
  };

  // Public shared report route — no auth required
  if (location.pathname.startsWith('/shared/')) {
    return (
      <React.Suspense fallback={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100dvh', fontFamily: 'system-ui', color: '#A09890' }}>
          Loading...
        </div>
      }>
        <Routes>
          <Route path="/shared/:shareToken" element={<SharedReportView />} />
        </Routes>
      </React.Suspense>
    );
  }

  // Participant new UI — owns its own auth flow + branded loading shell.
  // Admins can also visit this page intentionally for preview (via RoleSwitcher).
  // The join handler inside ParticipantNewPage already guards against admins
  // accidentally joining as participants via stale join links.
  if (location.pathname === '/p') {
    return <ParticipantNewPage />;
  }

  // Plain ember canvas while auth resolves — no animations, so the handoff
  // to ParticipantNewPage's LoadingShell (or researcher dashboard) is seamless.
  if (authLoading) {
    return (
      <div style={{ background: '#D4715A', width: '100%', height: '100dvh' }} />
    );
  }

  // Show login if not authenticated
  if (!user) {
    // If user is on join URL but not logged in, show login with join indicator
    if (isJoiningProject && joinProjectId) {
      const project = appData?.projects.find(p => p.id === joinProjectId);
      return (
        <div>
          {project && (
            <div className="bg-status-info text-white text-center py-3 px-4">
              <p className="font-bold">You're joining: {project.name}</p>
              <p className="text-sm">Please login or sign up to continue</p>
            </div>
          )}
          <LoginView />
        </div>
      );
    }
    return <LoginView />;
  }

  // Show holding screen if authenticated but not provisioned (no Claims).
  // Also wait for isLoadingData to settle — prevents flashing this screen
  // while Firestore data (which includes claims context) is still loading.
  // Guard: if data is still loading OR user exists but claims haven't arrived
  // yet (JWT refresh in progress), don't flash UnprovisionedView.
  // The old `!user` variant of this gate was unreachable (the login return
  // above already handled !user), which left a genuinely unprovisioned user
  // stranded on a bare ember canvas forever. Signed-in + claims absent +
  // data settled IS the unprovisioned state.
  // `claimsKnown` is the precise oracle for "the token has actually been read
  // for THIS user" — isLoadingData alone was a proxy that goes stale across
  // logout→login (the "Account not activated" flash during the JWT fetch).
  if (claimsKnown && !claimsRole && !isJoiningProject && !isLoadingData && user) {
    // A participant join that never completed (killed mid-flow, or the tab
    // was reopened from '/' — PWA start URL, bookmark, typed domain — instead
    // of '/p') would otherwise land here with claims still absent, and this
    // screen's only action is sign-out. ParticipantNewPage owns participant
    // joins (see the '/p' short-circuit above and its handlePendingJoin
    // effect), so hand off to it instead of stranding the user. Admin joins
    // are excluded — those are App's own JoinProject flow (isJoiningProject
    // above), not ParticipantNewPage's.
    const pendingJoin = safeLocalStorage.getItem('pendingProjectJoin');
    const pendingRole = safeLocalStorage.getItem('pendingJoinRole');
    if (pendingJoin && pendingRole !== 'admin') {
      return <Navigate to="/p" replace />;
    }
    return <UnprovisionedView />;
  }

  // RENDER ORDER (enforced explicitly):
  // 1. If joining a project, show join flow
  // 2. If participant with incomplete profile, show user info form
  // 3. If participant (via claims), render participant view
  // 4. Otherwise, render researcher view

  // 1. Show JoinProject UI if joining a project
  if (isJoiningProject && joinProjectId) {
    const project = appData?.projects.find(p => p.id === joinProjectId);
    return (
      <JoinProject
        projectId={joinProjectId}
        projectName={project?.name || null}
        status={joinStatus}
        onJoinComplete={() => {
          const joinRole = safeLocalStorage.getItem('pendingJoinRole') || 'participant';
          // Clear pending project join
          safeLocalStorage.removeItem('pendingProjectJoin');
          safeLocalStorage.removeItem('pendingJoinRole');
          setIsJoiningProject(false);
          // Route based on join role
          if (joinRole === 'participant') {
            navigate('/p');
          } else {
            navigate('/feed');
          }
        }}
      />
    );
  }

  // 2. Participant: always use the new participant UI
  if (claimsRole === 'participant') {
    return <Navigate to="/p" replace />;
  }

  // 3. Superadmin: redirect to /superadmin/ hub — never show researcher dashboard
  if (claimsRole === 'superadmin' && !location.pathname.startsWith('/superadmin')) {
    return <Navigate to="/superadmin" replace />;
  }

  // 4. Superadmin on /superadmin/* — render hub directly, skip researcher layout entirely
  if (claimsRole === 'superadmin' && location.pathname.startsWith('/superadmin')) {
    return (
      <div className="min-h-screen bg-white">
        <React.Suspense fallback={<div className="flex h-full items-center justify-center"><div className="w-8 h-8 border-3 border-t-action border-action/20 rounded-full animate-spin"></div></div>}>
        <Routes>
          <Route path="/superadmin/*" element={<SuperAdminGuard><SuperAdminHub /></SuperAdminGuard>} />
        </Routes>
        </React.Suspense>
      </div>
    );
  }

  // 5. Project chooser — show when researcher has projects but none selected
  // Only gate when no projectId has been chosen yet — once selected, let the dashboard render
  // even during data refreshes (prevents flash-back to loading screen)
  // Also gate when appData exists but projects haven't loaded yet (prevents dashboard flash)
  if ((claimsRole === 'researcher' || claimsRole === 'orgadmin') && !currentProject && !currentProjectId) {
    // Splash only while data is genuinely in flight. A loaded-but-empty project
    // list is a real state (every new org starts there) — it must fall through to
    // the chooser's create-project empty state, not spin forever.
    if (isLoadingData || !appData) {
      return <BootSplash />;
    }
    {
      return (
        <>
          <ProjectChooser
            projects={appData.projects}
            tasks={appData.tasks}
            participants={appData.participants}
            onSelect={(projectId: string) => {
              setCurrentProjectId(projectId);
              navigate('/feed');
            }}
            onCreateProject={() => setIsAddProjectModalOpen(true)}
            onLogout={logout}
            userName={userProfile?.displayName || user?.displayName || undefined}
          />
          {isAddProjectModalOpen && (
            <AddProjectModal
              onClose={() => setIsAddProjectModalOpen(false)}
              onCreateProject={handleCreateProject}
            />
          )}
        </>
      );
    }
  }

  const currentUserForHeader: Participant | TeamMember | null = userRole === 'participant' ? currentParticipant : currentResearcher || null;

  const isFeedRoute = location.pathname === '/feed';
  const shellBg = userRole === 'participant' ? 'bg-app-bg'
    : isFeedRoute ? (feedTheme === 'dark' ? 'bg-[#0A0A0A]' : 'bg-[#FAF8F5]')
    : 'bg-white';

  return (
    <div className={`flex h-[100dvh] ${shellBg} text-text-ink ${userRole !== 'participant' ? 'lg:pl-[52px]' : ''}`}>
      {(userRole === 'researcher' || userRole === 'orgadmin' || userRole === 'superadmin') && (
        <>
          <Sidebar
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
            currentUser={currentUserForHeader}
            onEnableNotifications={handleEnableNotifications}
            hasSignificationTasks={hasSignificationTasks}
          />
          {showResearcherOnboarding && <ResearcherOnboarding onComplete={handleOnboardingComplete} />}
        </>
      )}

      <div
        ref={scrollContainerRef}
        className={`flex flex-col flex-1 w-full h-full overflow-y-auto overflow-x-hidden relative ${shellBg} touch-pan-y`}
        style={{ overscrollBehaviorY: 'contain' }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Pull to Refresh Indicator */}
        <div
          className="absolute top-6 left-0 w-full flex justify-center items-start z-0 pointer-events-none"
          style={{
            opacity: pullChange > 0 ? 1 : 0,
            transition: 'opacity 0.2s'
          }}
        >
          <div
            className="bg-white p-2 rounded-full shadow-soft flex items-center justify-center transition-transform duration-200"
            style={{
              transform: `scale(${Math.min(pullChange / REFRESH_THRESHOLD, 1)})`
            }}
          >
            <RefreshCwIcon
              className={`w-5 h-5 ${isRefreshing ? 'text-action animate-spin' : 'text-action'}`}
              style={{ transform: !isRefreshing ? `rotate(${pullChange * 2}deg)` : undefined }}
            />
          </div>
        </div>

        {/* Connectivity Status Banner */}
        {!isOnline && (
          <div className="bg-action text-white px-4 py-2 text-xs font-bold flex items-center justify-center gap-2 animate-fade-in-down z-50 sticky top-0">
            <WifiOffIcon className="w-3.5 h-3.5" />
            Working Offline — Changes will sync when you're back online
          </div>
        )}

        {/* Content Wrapper */}
        <div
          className={`flex flex-col flex-grow min-h-full relative z-10 ${shellBg} transition-transform duration-200 ease-out`}
          style={{
            transform: (userRole === 'researcher' || userRole === 'orgadmin') && isSidebarOpen ? `translateX(280px) translateY(${pullChange}px)` : `translateY(${pullChange}px)`,
            width: (userRole === 'researcher' || userRole === 'orgadmin') && isSidebarOpen ? 'calc(100% - 280px)' : '100%'
          }}
        >
          <div className="max-w-[1536px] mx-auto w-full flex flex-col h-full flex-grow">
            <Header
              userRole={userRole}
              setUserRole={(role: UserRole) => {
                if (role === 'participant') {
                  // Slide admin view out to the left, then navigate
                  document.getElementById('root')?.classList.add('view-slide-out-left');
                  setTimeout(() => {
                    navigate('/p');
                    document.getElementById('root')?.classList.remove('view-slide-out-left');
                  }, 300);
                } else {
                  setUserRole(role);
                  navigate('/feed');
                }
              }}
              isSidebarOpen={isSidebarOpen}
              setIsSidebarOpen={setIsSidebarOpen}
              projects={appData?.projects || []}
              currentProject={currentProject}
              setCurrentProjectId={setCurrentProjectId}
              onOpenCreateProject={() => setIsAddProjectModalOpen(true)}
              onStartTour={handleStartTour}
              actualUserRole={claimsRole}
              isOnline={isOnline}
            />
            <main className={`flex-grow ${location.pathname === '/data' || location.pathname === '/feed' ? 'p-0' : 'px-4 sm:px-6 lg:px-8 py-6'} w-full`}>
              {(isLoadingData && isOnline) && !appData ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="w-16 h-16 border-4 border-t-action border-action/20 rounded-full animate-spin mb-4"></div>
                  <p className="text-text-ink font-bold text-lg">Loading Project Data...</p>
                  <p className="text-text-muted text-sm mt-2">Please wait while we fetch your data</p>
                </div>
              ) : (
                renderCurrentView()
              )}
            </main>
          </div>
        </div>
      </div>

      {selectedSubmission && appData && (
        <SubmissionModal
          /* Read the LIVE doc out of appData rather than the copy captured when
             the row was clicked. Anything written after opening — a transcript
             landing from transcribeSubmission, another researcher's tags —
             reaches the modal only through the submissions onSnapshot, and
             selectedSubmission is separate state that never hears it. Optimistic
             edits survive because handleUpdateSubmission patches appData too. */
          data={{
            submission: appData.submissions.find(s => s.id === selectedSubmission.id) || selectedSubmission,
            participant: projectParticipantsMap.get(selectedSubmission.participantId),
          }}
          onClose={() => { setSelectedSubmission(null); setModalSeekTime(undefined); setModalHighlight(undefined); }}
          currentProject={currentProject}
          onUpdateTags={handleUpdateTags}
          onUpdateNotes={handleUpdateNotes}
          onUpdateTranscript={handleUpdateTranscript}
          seekToTime={modalSeekTime}
          highlightQuote={modalHighlight}
          onShare={() => setSubmissionToShare(selectedSubmission)}
          taskName={selectedSubmission.taskId ? appData.tasks.find(t => t.id === selectedSubmission.taskId)?.prompt : undefined}
          onToggleReview={(id: string) => handleUpdateSubmission(id, { isReviewed: !selectedSubmission.isReviewed })}
        />
      )}
      {submissionToShare && appData && (
        <ShareSubmissionModal
          submission={submissionToShare}
          participant={projectParticipantsMap.get(submissionToShare.participantId)}
          onClose={() => setSubmissionToShare(null)}
        />
      )}

      {isAddProjectModalOpen && (
        <AddProjectModal
          onClose={() => setIsAddProjectModalOpen(false)}
          onCreateProject={handleCreateProject}
        />
      )}

      {isGroupModalOpen && (
        <AddGroupModal
          onClose={() => setIsGroupModalOpen(false)}
          onSaveGroup={handleSaveGroupWithClose}
          initialGroupData={editingGroup}
          allParticipants={projectParticipants}
        />
      )}

      {viewingParticipant && appData && (
        (() => {
          // Find email associated with participant
          const user = appData.users.find(u => viewingParticipant.id.startsWith(u.uid));
          // If no linked user, try to find in invitations if they are pending (unlikely if they are a participant but possible in some flows)
          const email = user?.email || viewingParticipant.email;

          // Find groups
          const groups = appData.participantGroups
            .filter(g => g.participantIds.includes(viewingParticipant.id))
            .map(g => g.name);

          // Calculate metrics
          const projectSubmissions = appData.submissions.filter(s =>
            s.participantId === viewingParticipant.id &&
            s.projectId === currentProject?.id
          );

          const submissionCount = projectSubmissions.length;

          // Calculate completion rate based on assigned tasks
          const activeTasks = appData.tasks.filter(t =>
            t.projectId === currentProject?.id &&
            t.status === 'active' &&
            (!t.assignedParticipantIds || t.assignedParticipantIds.includes(viewingParticipant.id))
          );

          const uniqueSubmittedTasks = new Set(projectSubmissions.map(s => s.taskId).filter(Boolean));
          const totalAssigned = activeTasks.length || 1;
          const completionRate = Math.min((uniqueSubmittedTasks.size / totalAssigned) * 100, 100);

          // Calculate last active
          let lastActive: string | undefined;
          if (projectSubmissions.length > 0) {
            const sorted = [...projectSubmissions].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
            const lastDate = new Date(sorted[0].timestamp);
            const now = new Date();
            const diffTime = Math.abs(now.getTime() - lastDate.getTime());
            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

            if (diffDays === 0) lastActive = 'Today';
            else if (diffDays === 1) lastActive = 'Yesterday';
            else lastActive = `${diffDays} days ago`;
          }

          return (
            <ParticipantProfileModal
              isOpen={true}
              participant={viewingParticipant}
              onClose={() => setViewingParticipant(null)}
              email={email}
              groups={groups}
              metrics={{
                submissionCount,
                completionRate,
                lastActive
              }}
            />
          );
        })()
      )}

      {showGlobalSuccess && (
        <div
          className="fixed top-5 left-1/2 -translate-x-1/2 z-[200] flex items-center gap-3 bg-white text-text-ink px-6 py-4 rounded-2xl shadow-float animate-fade-in-down"
          role="alert"
        >
          <div className="bg-status-info p-1 rounded-full text-black">
            <CheckIcon className="w-5 h-5 flex-shrink-0" />
          </div>
          <span className="font-bold text-sm">{showGlobalSuccess}</span>
        </div>
      )}

      {userRole !== 'participant' && <UploadManager uploads={activeUploads} />}
    </div>
  );
};

export default App;